/*!
Turbo 8.0.23
Copyright © 2026 37signals LLC
 */
class Rule {
  constructor({ handler, match = /.*/, except } = {}) {
    this.handler = handler;
    this.match = match;
    this.except = except;
  }

  matches(request) {
    return this.#matchesCondition(request, this.match) && !this.#matchesCondition(request, this.except)
  }

  #matchesCondition(request, condition) {
    if (!condition) return false
    if (typeof condition === 'function') return condition(request)

    const regexes = Array.isArray(condition) ? condition : [condition];
    return regexes.some(regex => regex.test(request.url))
  }

  async handle(event) {
    const { response, afterHandlePromise } = await this.handler.handle(event.request);
    event.waitUntil(afterHandlePromise);

    return response
  }
}

const DATABASE_NAME = "turbo-offline-database";
const DATABASE_VERSION = 1;
const STORE_NAME = "cache-registry";

function deleteCacheRegistries() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DATABASE_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  }).then(() => {
    cacheRegistryDatabase = null;
  })
}

class CacheRegistryDatabase {
  get(key) {
    const getOp = (store) => this.#requestToPromise(store.get(key));
    return this.#performOperation(STORE_NAME, getOp, "readonly")
  }

  has(key) {
    const countOp = (store) => this.#requestToPromise(store.count(key));
    return this.#performOperation(STORE_NAME, countOp, "readonly").then((result) => result === 1)
  }

  put(cacheName, key, value) {
    const putOp = (store) => {
      const item = { key: key, cacheName: cacheName, timestamp: Date.now(), ...value };
      store.put(item);
      return this.#requestToPromise(store.transaction)
    };

    return this.#performOperation(STORE_NAME, putOp, "readwrite")
  }

  getTimestamp(key) {
    return this.get(key).then((result) => result?.timestamp)
  }

  getOlderThan(cacheName, timestamp) {
    const getOlderThanOp = (store) => {
      const index = store.index("cacheNameAndTimestamp");
      const cursorRequest = index.openCursor(this.#getTimestampRange(cacheName, timestamp));

      return this.#cursorRequestToPromise(cursorRequest)
    };
    return this.#performOperation(STORE_NAME, getOlderThanOp, "readonly")
  }

  getEntryCount(cacheName) {
    const countOp = (store) => {
      const index = store.index("cacheNameAndTimestamp");
      const range = this.#getTimestampRange(cacheName);

      return this.#requestToPromise(index.count(range))
    };
    return this.#performOperation(STORE_NAME, countOp, "readonly")
  }

  getOldestEntries(cacheName, limit) {
    const getOldestOp = (store) => {
      const index = store.index("cacheNameAndTimestamp");
      const cursorRequest = index.openCursor(this.#getTimestampRange(cacheName));

      return this.#cursorRequestToPromise(cursorRequest, limit)
    };
    return this.#performOperation(STORE_NAME, getOldestOp, "readonly")
  }

  delete(key) {
    const deleteOp = (store) => this.#requestToPromise(store.delete(key));
    return this.#performOperation(STORE_NAME, deleteOp, "readwrite")
  }

  #performOperation(storeName, operation, mode) {
    return this.#openDatabase().then((database) => {
      const transaction = database.transaction(storeName, mode);
      const store = transaction.objectStore(storeName);
      return operation(store)
    })
  }

  #openDatabase() {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const cacheMetadataStore = request.result.createObjectStore(STORE_NAME, { keyPath: "key" });
      cacheMetadataStore.createIndex("cacheNameAndTimestamp", [ "cacheName", "timestamp" ]);
    };

    return this.#requestToPromise(request)
  }

  #requestToPromise(request) {
    return new Promise((resolve, reject) => {
      request.oncomplete = request.onsuccess = () => resolve(request.result);
      request.onabort = request.onerror = () => reject(request.error);
    })
  }

  #cursorRequestToPromise(request, limit = Infinity) {
    return new Promise((resolve, reject) => {
      const results = [];

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor && results.length < limit) {
          results.push(cursor.value);
          cursor.continue();
        } else {
          resolve(results);
        }
      };

      request.onerror = () => reject(request.error);
    })
  }

  #getTimestampRange(cacheName, upperBound = Infinity) {
    // Use compound key range: [cacheName, timestamp]
    return IDBKeyRange.bound(
      [cacheName, 0], // start of range
      [cacheName, upperBound], // end of range
      false, // lowerOpen: include lower bound
      true // upperOpen: exclude upper bound
    )
  }
}

let cacheRegistryDatabase = null;

function getDatabase() {
  if (!cacheRegistryDatabase) {
    cacheRegistryDatabase = new CacheRegistryDatabase();
  }
  return cacheRegistryDatabase
}

class CacheRegistry {
  constructor(cacheName) {
    this.cacheName = cacheName;
    this.database = getDatabase();
  }

  get(key) {
    return this.database.get(key)
  }

  has(key) {
    return this.database.has(key)
  }

  put(key, value = {}) {
    return this.database.put(this.cacheName, key, value)
  }

  getTimestamp(key) {
    return this.database.getTimestamp(key)
  }

  getOlderThan(timestamp) {
    return this.database.getOlderThan(this.cacheName, timestamp)
  }

  getEntryCount() {
    return this.database.getEntryCount(this.cacheName)
  }

  getOldestEntries(limit) {
    return this.database.getOldestEntries(this.cacheName, limit)
  }

  delete(key) {
    return this.database.delete(key)
  }
}

class ServiceWorker {
  #started = false
  #rules = []

  addRule(rule) {
    this.#rules.push(new Rule(rule));
  }

  start() {
    this.#warnIfNoRulesConfigured();

    if (!this.#started) {
      self.addEventListener("install", this.installed);
      self.addEventListener("message", this.messageReceived);
      self.addEventListener("fetch", this.fetch);
      this.#started = true;
    }
  }

  installed = (event) => {
    // Just log for now
    console.log("Service worker installed");
  }

  messageReceived = (event) => {
    if (this[event.data.action]) {
      const actionCall = this[event.data.action](event.data.params);
      event.waitUntil(actionCall);
    }
  }

  fetch = (event) => {
    if (this.#canInterceptRequest(event.request)) {
      const rule = this.#findMatchingRule(event.request);
      if (!rule) return

      const response = rule.handle(event);
      event.respondWith(response);
    }
  }

  async preloadResources({ urls }) {
    for (const url of urls) {
      const request = new Request(url);
      const rule = this.#findMatchingRule(request);
      if (!rule) continue

      try {
        const response = await fetch(url);
        await rule.handler.saveToCache(request, response);
      } catch (error) {
        console.debug(`Preloading failed for ${url}:`, error);
      }
    }
  }

  async clearCache() {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
    await deleteCacheRegistries();
  }

  #warnIfNoRulesConfigured() {
    if (this.#rules.length === 0) {
      console.warn("No rules configured for service worker. No requests will be intercepted.");
    }
  }

  #canInterceptRequest(request) {
    const url = new URL(request.url, location.href);
    return request.method === "GET" && url.protocol.startsWith('http')
  }

  #findMatchingRule(request) {
    return this.#rules.find((rule) => rule.matches(request))
  }
}

class CacheTrimmer {
  #isRunning = false

  constructor(cacheName, cacheRegistry, options = {}) {
    this.cacheName = cacheName;
    this.cacheRegistry = cacheRegistry;
    this.options = options;
  }

  async trim() {
    if (this.#isRunning) {
      return
    }

    if (!this.#shouldTrim()) {
      return
    }

    this.#isRunning = true;

    try {
      await this.deleteEntries();
    } finally {
      this.#isRunning = false;
    }
  }

  #shouldTrim() {
    const { maxAge, maxEntries } = this.options;
    return (maxAge && maxAge > 0) || (maxEntries && maxEntries > 0)
  }

  async deleteEntries() {
    // Order: age first, then count (age reduces the set for count trimming)
    if (this.options.maxAge) {
      await this.deleteEntriesByAge();
    }
    if (this.options.maxEntries) {
      await this.deleteEntriesByCount();
    }
  }

  async deleteEntriesByAge() {
    const maxAgeMs = this.options.maxAge * 1000;
    const cutoffTimestamp = Date.now() - maxAgeMs;

    const expiredEntries = await this.cacheRegistry.getOlderThan(cutoffTimestamp);

    if (expiredEntries.length === 0) {
      return
    }

    console.debug(`Trimming ${expiredEntries.length} expired entries from cache "${this.cacheName}"`);
    await this.#deleteEntryList(expiredEntries);
    console.debug(`Successfully trimmed ${expiredEntries.length} entries from cache "${this.cacheName}"`);
  }

  async deleteEntriesByCount() {
    const currentCount = await this.cacheRegistry.getEntryCount();
    const excess = currentCount - this.options.maxEntries;

    if (excess <= 0) {
      return
    }

    const entriesToDelete = await this.cacheRegistry.getOldestEntries(excess);

    if (entriesToDelete.length === 0) {
      return
    }

    console.debug(`Trimming ${entriesToDelete.length} entries (count limit) from cache "${this.cacheName}"`);
    await this.#deleteEntryList(entriesToDelete);
    console.debug(`Successfully trimmed ${entriesToDelete.length} entries from cache "${this.cacheName}"`);
  }

  async #deleteEntryList(entries) {
    const cache = await caches.open(this.cacheName);

    const deletePromises = entries.map(async (entry) => {
      const cacheDeletePromise = cache.delete(entry.key);
      const registryDeletePromise = this.cacheRegistry.delete(entry.key);

      return Promise.all([cacheDeletePromise, registryDeletePromise])
    });

    await Promise.all(deletePromises);
  }
}

/**
 * Builds a 206 Partial Content response from a cached response
 * when the request includes a Range header. This enables audio/video
 * streaming from the cache.
 *
 * @param {Request} request - The original request with Range header
 * @param {Response} response - The full cached response
 * @returns {Promise<Response>} - A 206 Partial Content response, or 416 on error
 */
async function buildPartialResponse(request, response) {
  // If already a partial response, return as-is
  if (response.status === 206) {
    return response
  }

  const rangeHeader = request.headers.get("range");
  if (!rangeHeader) {
    return response
  }

  // Can't handle Range requests for opaque responses (cross-origin)
  // since we can't read their body
  if (response.type === "opaque" || response.type === "opaqueredirect") {
    return response
  }

  try {
    const { start, end } = parseRangeHeader(rangeHeader);
    const blob = await response.blob();
    const { effectiveStart, effectiveEnd } = calculateEffectiveBoundaries(blob, start, end);

    const slicedBlob = blob.slice(effectiveStart, effectiveEnd);
    const slicedSize = slicedBlob.size;
    const totalSize = blob.size;

    const partialResponse = new Response(slicedBlob, {
      status: 206,
      statusText: "Partial Content",
      headers: response.headers
    });

    partialResponse.headers.set("Content-Length", slicedSize);
    partialResponse.headers.set("Content-Range", `bytes ${effectiveStart}-${effectiveEnd - 1}/${totalSize}`);

    return partialResponse
  } catch (error) {
    console.warn("Range request error:", error.message);
    return new Response("", { status: 416, statusText: "Range Not Satisfiable" })
  }
}

function parseRangeHeader(rangeHeader) {
  const normalized = rangeHeader.trim().toLowerCase();

  if (!normalized.startsWith("bytes=")) {
    throw new Error("Range unit must be 'bytes'")
  }

  // Only support single ranges, not multiple (e.g., bytes=0-99,200-299)
  if (normalized.includes(",")) {
    throw new Error("Multiple ranges are not supported")
  }

  const rangeValue = normalized.slice(6); // Remove "bytes="
  const match = rangeValue.match(/^(\d*)-(\d*)$/);

  if (!match) {
    throw new Error("Invalid range format")
  }

  const [, startStr, endStr] = match;
  const start = startStr ? parseInt(startStr, 10) : undefined;
  const end = endStr ? parseInt(endStr, 10) : undefined;

  if (start === undefined && end === undefined) {
    throw new Error("Invalid range: both start and end are missing")
  }

  return { start, end }
}

function calculateEffectiveBoundaries(blob, start, end) {
  const size = blob.size;

  let effectiveStart;
  let effectiveEnd;

  if (start !== undefined && end !== undefined) {
    // bytes=100-200 (both specified)
    effectiveStart = start;
    effectiveEnd = end + 1; // Range end is inclusive, slice end is exclusive
  } else if (start !== undefined) {
    // bytes=100- (from start to end of file)
    effectiveStart = start;
    effectiveEnd = size;
  } else {
    // bytes=-100 (last N bytes)
    effectiveStart = size - end;
    effectiveEnd = size;
  }

  // Validate boundaries
  if (effectiveStart < 0 || effectiveStart >= size || effectiveEnd > size) {
    throw new Error("Range not satisfiable")
  }

  return { effectiveStart, effectiveEnd }
}

class Handler {
  constructor({ cacheName, networkTimeout, maxAge, maxEntries, maxEntrySize, fetchOptions }) {
    this.cacheName = cacheName;
    this.networkTimeout = networkTimeout;
    this.fetchOptions = fetchOptions || {};
    this.maxEntrySize = maxEntrySize;

    this.cacheRegistry = new CacheRegistry(cacheName);
    this.cacheTrimmer = new CacheTrimmer(cacheName, this.cacheRegistry, { maxAge, maxEntries });
  }

  async handle(request) {
    // Abstract method
  }

  async fetchFromCache(request) {
    const cacheKeyUrl = buildCacheKey(request);
    let response = await caches.match(cacheKeyUrl, { ignoreVary: true });

    // Handle Range requests for cached responses (audio/video streaming)
    if (response !== undefined && request.headers.has("range")) {
      response = await buildPartialResponse(request, response);
    }

    if (response !== undefined && request.redirect === "manual" && response.redirected) {
      // Can't respond with a redirected response to a request
      // whose redirect mode is not "follow". Since we can't cache
      // the request with manual redirect mode because these might be
      // crawled in advance, when we don't know how these will be requested,
      // we just return the body here.
      response = new Response(response.body, { headers: response.headers, status: response.status, url: response.url });
    }

    return response
  }

  async fetchFromNetwork(request) {
    // Setting the referrer so that it doesn't appear as the service worker
    const referrer = request.referrer;
    return await fetch(request, { referrer, ...this.fetchOptions })
  }

  async saveToCache(request, response) {
    if (response && this.canCacheResponse(response)) {
      if (this.maxEntrySize && this.maxEntrySize > 0) {
        const size = await this.#getResponseSize(response);

        if (size === null) {
          console.warn(`Cannot determine size for opaque response to "${request.url}". Consider using fetchOptions: { mode: "cors" } if the server supports CORS. maxEntrySize check skipped.`);
        } else if (size > this.maxEntrySize) {
          console.debug(`Skipping cache for "${request.url}": response size ${size} exceeds maxEntrySize ${this.maxEntrySize}`);
          return
        }
      }

      const cacheKeyUrl = buildCacheKey(request, response);
      const cache = await caches.open(this.cacheName);

      const cachePromise = cache.put(cacheKeyUrl, response);
      const registryPromise = this.cacheRegistry.put(cacheKeyUrl);
      const trimPromise = this.cacheTrimmer.trim();

      return Promise.all([ cachePromise, registryPromise, trimPromise ]).catch(async (error) => {
        if (this.#isQuotaExceededError(error)) {
          await this.#clearAllStorage();
        }
        throw error
      })
    }
  }

  async #getResponseSize(response) {
    if (response.type === "opaque" || response.status === 0) {
      return null
    }

    const contentLength = response.headers.get("Content-Length");
    if (contentLength) {
      return parseInt(contentLength, 10)
    }

    const clone = response.clone();
    const blob = await clone.blob();
    return blob.size
  }

  canCacheResponse(response) {
    // OK response and opaque responses (due to CORS), that have a 0 status
    return response.status === 200 || response.status === 0
  }

  #isQuotaExceededError(error) {
    return error?.name === "QuotaExceededError" ||
      (error?.inner && error.inner.name === "QuotaExceededError")
  }

  async #clearAllStorage() {
    const cacheNames = await caches.keys();

    for (const cacheName of cacheNames) {
      await caches.delete(cacheName);
    }

    await deleteCacheRegistries();
  }
}

function buildCacheKey(requestOrUrl, response) {
  // If the response is HTML, cache always based on the final response's request url, so
  // that Turbo HTTP location redirects are not masked. Otherwise, cached based on the
  // original request url, so redirected images, resources, etc are available offline.
  // We don't have a response when responding from the cache because, so in that case,
  // we need to use the request's URL always. This is also the case when we want to
  // check the URL in our key-value store to see if we have a fresh version already.
  const request = new Request(requestOrUrl);
  const url = response && isHtmlResponse(response) ? response.url : request.url;
  return new URL(url, location.href).href
}

function isHtmlResponse(response) {
  return response.headers.get("content-type")?.includes("text/html")
}

/**
 * Cache-first: try the cache. If it's a hit, return that. If it's a miss,
 * fall back to network and then cache the response.
 */

class CacheFirst extends Handler {
  async handle(request) {
    let response = await this.fetchFromCache(request);
    let afterHandlePromise;

    if (response) {
      // Always trim the cache when using a response in cache-first strategy
      // because if we are always returning cached content and never saving
      // any new content, it will all eventually become older than maxAge
      afterHandlePromise = this.cacheTrimmer.trim();
      return { response, afterHandlePromise }
    }

    console.debug(`Cache miss for ${request.url}`);

    try {
      response = await this.fetchFromNetwork(request);
    } catch(error) {
      console.warn(
        `${error} fetching from network ${request.url}`
       );
    }

    if (response) {
      afterHandlePromise = this.saveToCache(request, response.clone());
    }

    return { response, afterHandlePromise }
  }

  canCacheResponse(response) {
    // Only cache OK responses, as we don't want to cache a network error
    // by mistake, which is impossible to distinguish from an opaque
    // response with status 0
    return response.status === 200
  }
}

/**
 * Network-first: try the network, with an optional time out, and if that fails
 * fallback to the cache
 */

class NetworkFirst extends Handler {
  async handle(request) {
    let response;
    let afterHandlePromise;
    let timeoutId;
    let cacheAttemptedOnTimeout = false;
    let responseFromNetwork = false;

    const networkPromise = this.fetchFromNetwork(request);
    const promises = [networkPromise];

    if (this.networkTimeout) {
      const timeoutPromise = new Promise((resolve) => {
        timeoutId = setTimeout(async () => {
          console.debug(`Network timeout after ${this.networkTimeout}s for ${request.url}, trying the cache...`);

          const cachedResponse = await this.fetchFromCache(request);
          cacheAttemptedOnTimeout = true;

          resolve(cachedResponse);
        }, this.networkTimeout * 1000);
      });
      promises.push(timeoutPromise);
    }

    try {
      // Either the network wins or the timeout wins. The network might win
      // with an error, though
      response = await Promise.race(promises);
      // If we got here without timeout, response is from network
      if (!cacheAttemptedOnTimeout) {
        responseFromNetwork = true;
      }
    } catch(error) {
      console.warn(
        `${error} fetching from network ${request.url} with timeout`
      );
    }

    if (timeoutId) clearTimeout(timeoutId);

    if (!response && cacheAttemptedOnTimeout) {
      // In this case we know we hit the timout and got a cache miss.
      // We can wait for the network promise just in case, as we don't have
      // anything to lose, knowing that we don't have the cache as fallback
      try {
        response = await networkPromise;
        responseFromNetwork = true;
      } catch(error) {
        // This might be the same error we got from the promise race
        console.warn(
          `${error} fetching from network ${request.url}`
        );
      }
    } else if (!response) {
      // If we didn't get a response and didn't try the cache, try it now
      // as a fallback
      response = await this.fetchFromCache(request);
    }

    // Only cache responses that came from the network, not from cache
    if (response && responseFromNetwork) {
      afterHandlePromise = this.saveToCache(request, response.clone());
    } else {
      afterHandlePromise = Promise.resolve();
    }

    return { response, afterHandlePromise }
  }
}

/**
 * Stale-while-revalidate: try the cache. If it's a hit, return that and
 * go to the network to fetch a new response and store it. If it's a miss,
 * just fall back to network and then store the response.
 */

class StaleWhileRevalidate extends Handler {
  async handle(request) {
    let response = await this.fetchFromCache(request);
    let afterHandlePromise;

    if (response) {
      afterHandlePromise = this.revalidateCache(request);
      return { response, afterHandlePromise }
    }

    console.debug(`Cache miss for ${request.url}`);

    try {
      response = await this.fetchFromNetwork(request);
    } catch(error) {
      console.warn(
        `${error} fetching from network ${request.url}`
      );
    }

    if (response) {
      afterHandlePromise = this.saveToCache(request, response.clone());
    } else {
      afterHandlePromise = Promise.resolve();
    }

    return { response, afterHandlePromise }
  }

  async revalidateCache(request) {
    try {
      const response = await this.fetchFromNetwork(request);
      if (response) {
        await this.saveToCache(request, response.clone());
      }
    } catch(error) {
      console.debug(`${error} revalidating cache for ${request.url}`);
    }
  }
}

const cacheFirst = (config) => new CacheFirst(config);
const networkFirst = (config) => new NetworkFirst(config);
const staleWhileRevalidate = (config) => new StaleWhileRevalidate(config);

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  cacheFirst: cacheFirst,
  networkFirst: networkFirst,
  staleWhileRevalidate: staleWhileRevalidate
});

const serviceWorker = new ServiceWorker();

function addRule(rule) {
  serviceWorker.addRule(rule);
}

function start() {
  serviceWorker.start();
}

export { ServiceWorker, addRule, index as handlers, serviceWorker, start };
